import { v4 as uuid } from 'uuid';
import {
  Box, Grid,
} from '@mui/material';
import { useCallback, useContext, useMemo } from 'react';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import ImageComponent from '../../ImageComponent';
import { ImagesContext } from '../../../contexts/ImagesContext';
import ImageModal from '../../Modals/ImageModal/ImageModal';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import styles from './BlockCard.module.css';
import BlockCardData, { BlockCardVariant } from '../../../models/Block/BlockCardData';
import useResponsive from '../../../hooks/UseResponsive';

export interface BlockCardProps extends BlockProps {
  blockData: BlockData[]
}

function BlockCard({
  data, isEditing, isTemplate, blockData, editHandler,
}: BlockCardProps) {
  const props = (data.data as BlockCardData);
  const { images } = useContext(ImagesContext);
  const cardItemId = uuid();

  const deleteImage = useCallback(() => {
    props.mainImage = undefined;
    editHandler(data.id, props);
  }, [props, editHandler, data.id]);

  useResponsive({
    items: [
      {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-image-${cardItemId}`,
        queries: [
          { size: 0, className: styles.imageSideSmartphonePreview },
          { size: 600, className: styles.imageSide },
        ],
      },
      {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-header-${cardItemId}`,
        queries: [
          { size: 0, className: styles.headerSmartphonePreview },
          { size: 600, className: styles.header },
        ],
      },
      {
        wrapperId: `card-header-${cardItemId}`,
        targetId: `card-title-${cardItemId}`,
        queries: [
          { size: 0, className: styles.titleSmartphonePreview },
          { size: 600, className: styles.title },
        ],
      },

      props.variant === BlockCardVariant.Left
        ? {
          wrapperId: `card-wrapper-${cardItemId}`,
          targetId: `card-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideLeftSmartphonePreview },
            { size: 600, className: styles.textSideLeft },
          ],
        }
        : {
          wrapperId: `card-wrapper-${cardItemId}`,
          targetId: `card-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideRightSmartphonePreview },
            { size: 600, className: styles.textSideRight },
          ],
        },
      props.variant === BlockCardVariant.Right && {
        wrapperId: `card-wrapper-${cardItemId}`,
        targetId: `card-wrapper-${cardItemId}`,
        queries: [
          { size: 0, className: styles.containerSmartphonePreview },
          { size: 600, className: styles.container },
        ],
      },
    ],
  });

  const imageSide = useMemo(() => (
    <Grid
      id={`card-image-${cardItemId}`}
      item
      xs={6}
      minWidth={300}
    >

      {props.mainImage && (
        <Box sx={Utils.hoverBorder(isEditing)}>

          {isEditing && <CloseButton updateHandler={() => { deleteImage(); }} />}
          <ImageModal
            disabled={isTemplate || !isEditing}
            images={images}
            handleOnConfirm={(url: string) => {
              props.mainImage.value = url;
              editHandler(data.id, props);
            }}
          >
            <ImageComponent src={props.mainImage.value} />
          </ImageModal>
        </Box>
      )}

    </Grid>
  ), [cardItemId, data.id, deleteImage, editHandler, images, isEditing, isTemplate, props]);

  const textSide = useMemo(() => (
    <Grid
      id={`card-text-${cardItemId}`}
      item
      xs={6}
      minWidth={300}
      alignItems="start"
      justifyContent="start"
    >
      {props.descriptionHeader && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
          <CloseButton
            updateHandler={() => {
              props.descriptionHeader = undefined;
              editHandler(data.id, props);
            }}
          />
          )}

          <EditableText
            text={props.descriptionHeader.value}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionHeader.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}
      {props.descriptionSubHeader && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
          <CloseButton
            updateHandler={() => {
              props.descriptionSubHeader = undefined;
              editHandler(data.id, props);
            }}
          />
          )}

          <EditableText
            text={props.descriptionSubHeader.value}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionSubHeader.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}
      {props.descriptionBody && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton
              updateHandler={() => {
                props.descriptionBody = undefined;
                editHandler(data.id, props);
              }}
            />
          )}

          <EditableText
            text={props.descriptionBody.value}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.descriptionBody.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}

      {props.button && (
      <Box sx={Utils.hoverBorder(isEditing)}>
        <CloseButton
          show={isEditing}
          updateHandler={() => {
            props.button = undefined;
            editHandler(data.id, props);
          }}
        />
        <LinkButton
          button={props.button}
          blockData={blockData}
          isEditable={isEditing && !isTemplate}
          isTemplate={isTemplate}
          updateHandler={(item) => {
            props.button = item;
            editHandler(data.id, props);
          }}
        />
      </Box>
      )}
    </Grid>
  ), [blockData, cardItemId, data.id, editHandler, isEditing, isTemplate, props]);

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '250px',
        overflow: 'hidden',
        backgroundImage: props.backgroundImage ? `url(${props.backgroundImage.value})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: 2,
      }}
    >
      <Grid
        id={`card-header-${cardItemId}`}
        container
        spacing={0}
        direction="column"
        justifyContent="end"
      >

        {(isEditing && !isTemplate) && (
          <ImageModal
            title="背景設定"
            images={images}
            handleOnConfirm={(url: string) => {
              props.backgroundImage.value = url;
              editHandler(data.id, props);
            }}
          />
        )}

        <Grid item xs={12} sx={{ paddingTop: 1 }} />

        {props.title && (
          <Grid
            id={`card-title-${cardItemId}`}
            item
            xs={12}
            alignContent="start"
            justifyContent="start"
            sx={{
              paddingTop: 2,
              maxHeight: '100%',
              height: '100%',
            }}
          >
            <Box sx={Utils.hoverBorder(isEditing)}>

              {isEditing && (
                <CloseButton
                  updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                />
              )}

              <EditableText
                text={props.title.value}
                isEditable={isEditing && !isTemplate}
                updateHandler={(text) => {
                  props.title.value = text;
                  editHandler(data.id, props);
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid
        id={`card-wrapper-${cardItemId}`}
        container
        spacing={0}
        direction="row"
        alignItems="stretch"
        justifyContent="center"
        sx={{
          paddingTop: 2,
        }}
      >
        {props.variant === BlockCardVariant.Left ? (
          <>
            {imageSide}
            {textSide}
          </>
        ) : (
          <>
            {textSide}
            {imageSide}
          </>
        )}
      </Grid>

    </Grid>
  );
}

export default BlockCard;
